import React, { useEffect, useMemo, useRef, useState } from "react";
import { ResellerOption } from "../../model/Campaign";

interface ResellerComboboxProps {
  id: string;
  value: string;
  onChange: (erpRef: string) => void;
  options: ResellerOption[];
  disabled?: boolean;
  placeholder?: string;
}

const labelOf = (r: ResellerOption): string =>
  r.name ? `${r.name} (${r.erp_ref})` : r.erp_ref;

const MAX_RESULTS = 50;

/**
 * Searchable reseller picker: a combobox with typeahead, keyboard navigation
 * (Up/Down/Enter/Escape) and an ARIA listbox. The value is always an existing
 * `erp_ref`; free text is not accepted (blur without a selection reverts).
 */
export default function ResellerCombobox({
  id,
  value,
  onChange,
  options,
  disabled,
  placeholder,
}: ResellerComboboxProps) {
  const selected = options.find((o) => o.erp_ref === value) ?? null;
  const [query, setQuery] = useState(selected ? labelOf(selected) : "");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);

  // Reflect an externally-set value (e.g. reset).
  useEffect(() => {
    const current = options.find((o) => o.erp_ref === value);
    setQuery(current ? labelOf(current) : "");
  }, [value, options]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const pool = q
      ? options.filter((o) =>
          `${o.erp_ref} ${o.name ?? ""}`.toLowerCase().includes(q),
        )
      : options;
    return pool.slice(0, MAX_RESULTS);
  }, [query, options]);

  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, []);

  const select = (r: ResellerOption) => {
    onChange(r.erp_ref);
    setQuery(labelOf(r));
    setOpen(false);
  };

  const handleBlur = () => {
    // No free text: revert to the selected label, or clear if emptied.
    if (query.trim() === "") {
      onChange("");
    } else if (selected) {
      setQuery(labelOf(selected));
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        setHighlight(0);
      } else {
        setHighlight((h) => Math.min(h + 1, filtered.length - 1));
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      if (open && filtered[highlight]) {
        e.preventDefault();
        select(filtered[highlight]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const activeOption = open ? filtered[highlight] : undefined;

  return (
    <div ref={rootRef} className="relative">
      <input
        id={id}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={`${id}-listbox`}
        aria-autocomplete="list"
        aria-activedescendant={
          activeOption ? `${id}-opt-${activeOption.erp_ref}` : undefined
        }
        autoComplete="off"
        disabled={disabled}
        value={query}
        placeholder={placeholder ?? "Procurar reseller…"}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setHighlight(0);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        onBlur={handleBlur}
        className="w-full border rounded-lg px-3 py-2 disabled:bg-gray-100"
      />
      {open && !disabled && (
        <ul
          id={`${id}-listbox`}
          role="listbox"
          aria-label="Resellers"
          className="absolute z-50 mt-1 max-h-64 w-full overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg"
        >
          {filtered.length === 0 ? (
            <li className="px-3 py-2 text-sm text-gray-500">Sem resultados.</li>
          ) : (
            filtered.map((r, i) => (
              <li
                key={r.erp_ref}
                id={`${id}-opt-${r.erp_ref}`}
                role="option"
                aria-selected={r.erp_ref === value}
                onMouseEnter={() => setHighlight(i)}
                onMouseDown={(e) => {
                  // Keep focus on the input so blur-revert doesn't fire first.
                  e.preventDefault();
                  select(r);
                }}
                className={`cursor-pointer px-3 py-2 text-sm ${
                  i === highlight ? "bg-blue-50" : ""
                } ${r.erp_ref === value ? "font-medium text-blue-700" : "text-gray-800"}`}
              >
                {labelOf(r)}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
